import {
  EquirectangularReflectionMapping,
  HalfFloatType,
  LoadingManager,
  PMREMGenerator,
  Scene,
  Texture,
  WebGLRenderTarget,
  WebGLRenderer,
} from 'three'
import { HDRLoader } from 'three/examples/jsm/loaders/HDRLoader.js'

export interface HDRIEnvironmentOptions {
  backgroundIntensity: number
  environmentIntensity: number
  rotationY: number
}

export interface HDRIEnvironmentMetrics {
  width: number
  height: number
}

const textureMetrics = (texture: Texture): HDRIEnvironmentMetrics => {
  const image = texture.image as { width?: unknown; height?: unknown }
  if (
    typeof image.width !== 'number'
    || typeof image.height !== 'number'
    || image.width <= 0
    || image.height <= 0
  ) throw new Error('The decoded HDR environment has invalid dimensions.')
  return { width: image.width, height: image.height }
}

/** Owns one equirectangular HDR background and its PMREM lighting texture. */
export class HDRIEnvironment {
  private readonly loader: HDRLoader
  private readonly renderer: WebGLRenderer
  private source: Texture | null = null
  private pmremTarget: WebGLRenderTarget | null = null
  private metrics: HDRIEnvironmentMetrics | null = null
  private loadPromise: Promise<HDRIEnvironmentMetrics> | null = null

  public constructor(manager: LoadingManager, renderer: WebGLRenderer) {
    this.loader = new HDRLoader(manager).setDataType(HalfFloatType)
    this.renderer = renderer
  }

  public prepare(url: string): Promise<HDRIEnvironmentMetrics> {
    if (this.source && this.metrics) return Promise.resolve(this.metrics)
    if (this.loadPromise) return this.loadPromise

    this.loadPromise = this.loader.loadAsync(url)
      .then((source) => {
        source.name = 'ElinaEveningPureSky1K'
        source.mapping = EquirectangularReflectionMapping
        source.needsUpdate = true

        const generator = new PMREMGenerator(this.renderer)
        try {
          generator.compileEquirectangularShader()
          this.pmremTarget = generator.fromEquirectangular(source)
          this.pmremTarget.texture.name = 'ElinaEveningPureSkyPMREM'
        } finally {
          generator.dispose()
        }

        this.source = source
        this.metrics = textureMetrics(source)
        return this.metrics
      })
      .catch((error: unknown) => {
        this.loadPromise = null
        this.source?.dispose()
        this.pmremTarget?.dispose()
        this.source = null
        this.pmremTarget = null
        this.metrics = null
        throw error
      })

    return this.loadPromise
  }

  public apply(scene: Scene, options: HDRIEnvironmentOptions): boolean {
    if (!this.source || !this.pmremTarget) return false

    scene.background = this.source
    scene.backgroundBlurriness = 0
    scene.backgroundIntensity = Math.max(0, options.backgroundIntensity)
    scene.backgroundRotation.set(0, options.rotationY, 0)
    scene.environment = this.pmremTarget.texture
    scene.environmentIntensity = Math.max(0, options.environmentIntensity)
    scene.environmentRotation.set(0, options.rotationY, 0)
    return true
  }

  public detach(scene: Scene): void {
    if (scene.background === this.source) scene.background = null
    if (scene.environment === this.pmremTarget?.texture) scene.environment = null
  }

  public dispose(scene?: Scene): void {
    if (scene) this.detach(scene)
    this.source?.dispose()
    this.pmremTarget?.dispose()
    this.source = null
    this.pmremTarget = null
    this.metrics = null
    this.loadPromise = null
  }
}
